import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import * as Models from 'app/_shared/models/models-index';
import * as Enums from 'app/app.enums';
import moment from 'moment';
import { AuthenticationService } from 'app/_shared/services';
import { Filter } from 'app/_shared/models/models-index';
import { FacetedFilterDataValueType, IFacetedFilterDataItemValue, IFacetedFilterResult } from 'app/filterPanel/types';

export interface FilterMonth {
  startDate: Date;
  endDate: Date;
  label: string;
};

export interface FilterRetailer {
  id: number;
}

export interface FilterProgram {
  id: number;
};

@Injectable()
export class FilterService {
  private enums = Enums.Enums;

  public updatedSubject: BehaviorSubject<Models.FilterState> = new BehaviorSubject<Models.FilterState>(this.getDefaultFilterState());

  constructor(
    private authService: AuthenticationService
  ) {
    // this.updatedSubject = new BehaviorSubject(this.getDefaultFilterState());
  }

  updateDates(startDate: Date, endDate: Date, label?: string) {
    console.log(`Updating filter dates: StartDate: ${moment(startDate).format('MM/DD/YYYY')}
         - EndDate: ${moment(endDate).format('MM/DD/YYYY')} - Label: ${label}`);

    const current = this.updatedSubject.value;

    current.startDate = startDate;
    current.endDate = endDate;
    current.dateLabel = label;

    this.updatedSubject.next(current);
  }

  updateRetailer(filter: FilterRetailer) {
    console.log(`Updating retailer: ${JSON.stringify(filter)}`);
    const currentFilterState = this.updatedSubject.value;

    // remove current retailer filter if there is one
    const retailerFilteredOut = currentFilterState.filters.filter(f => f.type !== 'RetailerId');

    if (filter.id > 0) {
      retailerFilteredOut.push({ type: 'RetailerId', value: filter.id.toString() });
    }

    currentFilterState.filters = retailerFilteredOut;
    this.updatedSubject.next(currentFilterState);
  }

  updateRetailersAndOrgLookups(facetedFilters: IFacetedFilterResult[]) {
    const filters = this.mergeFilters(facetedFilters);

    // remove any existing filters for retailer / orglookups first
    const currentFilterState = this.updatedSubject.value;
    const currentFilters = currentFilterState.filters.filter(f =>
      f.type !== FacetedFilterDataValueType.Retailer &&
      f.type !== FacetedFilterDataValueType.OrgCode1 &&
      f.type !== FacetedFilterDataValueType.OrgCode2 &&
      f.type !== FacetedFilterDataValueType.OrgCode3);

    const newFilters = currentFilters.concat(filters);
    currentFilterState.filters = newFilters;
    this.updatedSubject.next(currentFilterState);
  }

  updateProgram(filter: FilterProgram) {
    const currentFilterState = this.updatedSubject.value;

    // remove current program filter if there is one
    const programFilteredOut = currentFilterState.filters.filter(f => f.type !== 'ProgramId');

    if (filter.id > 0) {
      programFilteredOut.push({ type: 'ProgramId', value: filter.id.toString() });
    }

    currentFilterState.filters = programFilteredOut;
    this.updatedSubject.next(currentFilterState);
  }

  updateDatesByMode(dateMode: Enums.IDateMode) {
    const [startDate, endDate, label] = this.getDateRange(dateMode.dateModeId);

    this.updateDates(startDate, endDate, label);
  }

  getMonths(): FilterMonth[] {
    let startDate = moment().startOf('month').subtract(1, 'year');
    const endDate = moment().startOf('month').add(1, 'month');

    const months = new Set<FilterMonth>();

    while (startDate < endDate) {
      months.add({
        startDate: startDate.toDate(),
        endDate: startDate.clone().endOf('month').toDate(),//.add(1, 'month').toDate(),
        label: startDate.format('MMM YYYY')
      });

      startDate = startDate.add(1, 'month');
    }

    return Array.from(months);
  }

  getTrendKpiMonths(): FilterMonth[] {
    const months = new Set<FilterMonth>();
    let currentFilterState = this.updatedSubject.value;
    months.add({
      startDate: currentFilterState.startDate,
      endDate: currentFilterState.endDate,
      label: 'Dashboard Time Period'
    });
    months.add({
      startDate: moment(currentFilterState.startDate).startOf('month').subtract(1, 'year').toDate(),
      endDate: currentFilterState.endDate,
      label: 'Last 12 Months'
    });

    return Array.from(months);
  }

  getDateModes(): Enums.IDateMode[] {
    const dateModes: Enums.IDateMode[] = [
      this.enums.dateModes.today,
      this.enums.dateModes.yesterday,
      this.enums.dateModes.currentMonth,
      this.enums.dateModes.lastMonth,
      this.enums.dateModes.lastThirtyDays,
      this.enums.dateModes.previousThreeMonths,
      this.enums.dateModes.previousTwelveMonths
      // this.enums.dateModes.custom
    ];

    return dateModes;
  }

  private mergeFilters(filters: IFacetedFilterResult[]): Models.Filter[] {
    // convert and merge FacetedFilters from filter pop panel to standard api filters
    const createDefaultFilter = (filterType: string): Models.Filter => {
      return {
        type: filterType,
        value: '',
        values: []
      };
    };

    const retailerFilters = createDefaultFilter(FacetedFilterDataValueType.Retailer);
    const org1Filters = createDefaultFilter(FacetedFilterDataValueType.OrgCode1);
    const org2Filters = createDefaultFilter(FacetedFilterDataValueType.OrgCode2);
    const org3Filters = createDefaultFilter(FacetedFilterDataValueType.OrgCode3);

    filters.forEach(filter => {
      const filterValues = JSON.parse(filter.value) as IFacetedFilterDataItemValue[];
      if (filterValues && filterValues.length) {
        filterValues.forEach(filterValue => {
          switch (filterValue.name) {
            case FacetedFilterDataValueType.OrgCode1:
              org1Filters.values.push(filterValue.key);
              break;
            case FacetedFilterDataValueType.OrgCode2:
              org2Filters.values.push(filterValue.key);
              break;
            case FacetedFilterDataValueType.OrgCode3:
              org3Filters.values.push(filterValue.key);
              break;
            case FacetedFilterDataValueType.Retailer:
              retailerFilters.values.push(filterValue.key);
              break;
            default:
              console.log('mergeFilters, filter not found ' + filterValue.name);
          }
        });
      }
    });

    // add any filter types that have any values
    const allFilters: Models.Filter[] = [];
    [retailerFilters, org1Filters, org2Filters, org3Filters].forEach(filter => {
      if (filter.values.length) {
        filter.value = filter.values.join(',');
        allFilters.push(filter);
      }
    });

    return allFilters;
  }

  private getDateRange(dateModeId: number): [Date, Date, string] {
    let startDate: Date;
    let endDate: Date;
    let label: string;
    const today = new Date();

    switch (dateModeId) {
      case this.enums.dateModes.today.dateModeId:
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        label = 'Today';
        break;
      case this.enums.dateModes.yesterday.dateModeId:
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
        label = 'Yesterday';
        break;
      case this.enums.dateModes.lastThirtyDays.dateModeId:
        endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
        startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30);
        label = 'Last 30 Days';
        break;
      case this.enums.dateModes.lastMonth.dateModeId:
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        label = 'Last Month';
        break;
      case this.enums.dateModes.previousThreeMonths.dateModeId:
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        startDate = new Date(today.getFullYear(), today.getMonth() - 3, 1);
        label = 'Previous 3 Months';
        break;
      default: // current month
        endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        label = 'Current Month';
        break;
    }

    return [startDate, endDate, label];
  }

  getDefaultFilterState(): Models.FilterState {
    const [startDate, endDate, label] = this.getDateRange(this.enums.dateModes.currentMonth.dateModeId);

    const currentUser = this.authService.getCurrentUser();

    const filters: Filter[] = [];

    // Default to program 1
    filters.push({ type: 'ProgramId', value: '1' });

    if (currentUser.retailerId) {
      filters.push({ type: 'RetailerId', value: currentUser.retailerId.toString() });
    }

    return {
      startDate: startDate,
      endDate: endDate,
      filters: filters,
      dateLabel: label
    };
  }

  resetFilterState() {
    this.updatedSubject.next(this.getDefaultFilterState());
  }
}
